import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Stack, Chip, Divider, Button } from '@mui/material';
import { supabase } from '../../supabaseClient';
import { serifFont } from '../../theme';

interface StopRow {
  stop_id: string;
  title: string;
  address: string | null;
  context: string | null;
  icon: string | null;
}

interface ChatRow {
  chat_id: string;
  role: 'user' | 'assistant';
  content: string;
  pinned: boolean;
  created_at: string;
}

interface OutputRow {
  output_id: string;
  prompt_type: string;
  content: string;
}

interface GuideItem {
  masterpiece_id: string;
  rank: number;
  tier: string;
  kind: string;
  name: string;
  artist: string | null;
  date_or_period: string | null;
  instruction: string | null;
  why_it_matters: string | null;
  image_url: string | null;
  found_photo_id: string | null;
}

interface ChatDisplay {
  chat_id: string;
  role: 'user' | 'assistant';
  text: string;
  photo_urls: string[];
  pinned: boolean;
}

// Render **bold** and *italic* in plain text. Bold first so inner asterisks of
// a bold span don't get re-matched as italic.
function renderRich(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const bold = text.split(/\*\*(.+?)\*\*/g);
  bold.forEach((bp, bi) => {
    if (bi % 2 === 1) {
      out.push(<strong key={`b${bi}`}>{bp}</strong>);
    } else {
      const it = bp.split(/\*(.+?)\*/g);
      it.forEach((part, ii) => {
        if (ii % 2 === 1) out.push(<em key={`b${bi}i${ii}`}>{part}</em>);
        else out.push(part);
      });
    }
  });
  return out;
}

function parseChatContent(content: string): { storagePaths: string[]; text: string } {
  const lines = content.split('\n');
  const storagePaths: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const m = lines[i].match(/^__photo:(.+?)__$/);
    if (m) {
      storagePaths.push(m[1]);
      i++;
    } else break;
  }
  return { storagePaths, text: lines.slice(i).join('\n').trim() };
}

export default function StopDetailPage() {
  const { stopId } = useParams<{ stopId: string }>();
  const [stop, setStop] = useState<StopRow | null>(null);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [chats, setChats] = useState<ChatDisplay[]>([]);
  const [outputs, setOutputs] = useState<OutputRow[]>([]);
  const [guideItems, setGuideItems] = useState<GuideItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!stopId) return;
    (async () => {
      // 1. Stop + photos in parallel
      const [stopRes, photosRes, chatsRes, outputsRes, guideRes] = await Promise.all([
        supabase
          .from('stops')
          .select('stop_id, title, address, context, icon')
          .eq('stop_id', stopId)
          .maybeSingle(),
        supabase
          .from('photos')
          .select('photo_id, storage_path, taken_at')
          .eq('stop_id', stopId)
          .order('taken_at', { ascending: true, nullsFirst: false }),
        supabase
          .from('chats')
          .select('chat_id, role, content, pinned, created_at')
          .eq('entity_type', 'stop')
          .eq('entity_id', stopId)
          .order('created_at', { ascending: true })
          .limit(500),
        supabase
          .from('outputs')
          .select('output_id, prompt_type, content')
          .eq('stop_id', stopId)
          .order('created_at', { ascending: false }),
        supabase
          .from('plan_masterpieces')
          .select('masterpiece_id, rank, tier, kind, name, artist, date_or_period, instruction, why_it_matters, image_url, found_photo_id')
          .eq('stop_id', stopId)
          .order('rank', { ascending: true }),
      ]);

      setStop((stopRes.data as StopRow | null) ?? null);

      const photos = (photosRes.data ?? []) as { photo_id: string; storage_path: string }[];
      const pathToUrl = new Map<string, string>();
      const gridUrls: string[] = [];
      if (photos.length > 0) {
        const paths = photos.map((p) => p.storage_path);
        const { data: signed } = await supabase.storage.from('stop-photos').createSignedUrls(paths, 3600);
        (signed ?? []).forEach((s: any) => {
          if (s.path && s.signedUrl) {
            pathToUrl.set(s.path, s.signedUrl);
            gridUrls.push(s.signedUrl);
          }
        });
      }
      setPhotoUrls(gridUrls);

      // Resolve chat photo references against the signed-URL map
      const chatRows = (chatsRes.data ?? []) as ChatRow[];
      const displayChats: ChatDisplay[] = chatRows.map((c) => {
        const { storagePaths, text } = parseChatContent(c.content);
        return {
          chat_id: c.chat_id,
          role: c.role,
          text,
          photo_urls: storagePaths.map((p) => pathToUrl.get(p)).filter((u): u is string => !!u),
          pinned: c.pinned,
        };
      });
      setChats(displayChats);

      setOutputs((outputsRes.data ?? []) as OutputRow[]);

      // Resolve user-attached photos for guide items where found_photo_id exists
      const guideRows = (guideRes.data ?? []) as GuideItem[];
      const photoIdsNeeded = guideRows.map((g) => g.found_photo_id).filter((x): x is string => !!x);
      let userPhotoByPhotoId: Record<string, string> = {};
      if (photoIdsNeeded.length > 0) {
        const { data: gPhotos } = await supabase
          .from('photos')
          .select('photo_id, storage_path')
          .in('photo_id', photoIdsNeeded);
        const gPaths = (gPhotos ?? []).map((p: any) => p.storage_path as string);
        if (gPaths.length > 0) {
          const { data: gSigned } = await supabase.storage.from('stop-photos').createSignedUrls(gPaths, 3600);
          const pathById: Record<string, string> = Object.fromEntries(
            (gPhotos ?? []).map((p: any) => [p.photo_id, p.storage_path]),
          );
          const urlByPath: Record<string, string> = Object.fromEntries(
            (gSigned ?? []).map((s: any) => [s.path, s.signedUrl]),
          );
          for (const id of photoIdsNeeded) {
            const path = pathById[id];
            if (path && urlByPath[path]) userPhotoByPhotoId[id] = urlByPath[path];
          }
        }
      }
      setGuideItems(
        guideRows.map((g) => ({
          ...g,
          image_url: g.found_photo_id && userPhotoByPhotoId[g.found_photo_id]
            ? userPhotoByPhotoId[g.found_photo_id]
            : g.image_url,
        })),
      );

      setLoading(false);
    })();
  }, [stopId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!stop) return <Typography color="text.secondary">Place not found.</Typography>;

  const mustSee = guideItems.filter((g) => g.tier !== 'important');
  const important = guideItems.filter((g) => g.tier === 'important');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary">Place</Typography>
          <Typography variant="h4" sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}>
            {stop.icon ? `${stop.icon}  ` : ''}{stop.title}
          </Typography>
        </Box>
        <Button component={Link} to={`/stops/${stopId}/edit`} variant="outlined" size="small">
          Edit
        </Button>
      </Box>
      {stop.address ? <Typography color="text.secondary" sx={{ mb: 2 }}>{stop.address}</Typography> : null}
      {stop.context ? (
        <Box sx={{ borderLeft: '3px solid', borderColor: 'secondary.main', pl: 2, my: 3 }}>
          <Typography sx={{ fontFamily: serifFont, fontStyle: 'italic' }}>{stop.context}</Typography>
        </Box>
      ) : null}

      {photoUrls.length > 0 ? (
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Typography variant="overline" color="text.secondary">Photos · {photoUrls.length}</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
              gap: 1,
            }}
          >
            {photoUrls.map((url, i) => (
              <Box key={i} sx={{ aspectRatio: '1 / 1', overflow: 'hidden', borderRadius: 1, bgcolor: 'divider' }}>
                <img
                  src={url}
                  alt=""
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    imageOrientation: 'from-image',
                  }}
                />
              </Box>
            ))}
          </Box>
        </Stack>
      ) : null}

      {chats.length > 0 ? (
        <Box sx={{ mt: 5 }}>
          <Typography variant="overline" color="text.secondary">Conversations</Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {chats.map((c) => (
              <Box
                key={c.chat_id}
                sx={{ display: 'flex', gap: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                {c.photo_urls[0] ? (
                  <Box sx={{ flex: '0 0 96px' }}>
                    <img
                      src={c.photo_urls[0]}
                      alt=""
                      loading="lazy"
                      style={{
                        width: 96,
                        height: 96,
                        objectFit: 'cover',
                        borderRadius: 6,
                        display: 'block',
                        imageOrientation: 'from-image',
                      }}
                    />
                  </Box>
                ) : null}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
                  >
                    {c.role === 'assistant' ? 'Roamoir' : 'You'}
                    {c.pinned ? (
                      <Chip label="PINNED" size="small" color="secondary" sx={{ ml: 1, height: 18 }} />
                    ) : null}
                  </Typography>
                  <Typography
                    sx={
                      c.role === 'assistant'
                        ? { fontFamily: serifFont, fontStyle: 'italic', whiteSpace: 'pre-wrap', mt: 0.5 }
                        : { whiteSpace: 'pre-wrap', mt: 0.5 }
                    }
                  >
                    {renderRich(c.text)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}

      {guideItems.length > 0 ? (
        <Box sx={{ mt: 5 }}>
          <Typography variant="overline" color="text.secondary">Guide</Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {mustSee.map((g, i) => <GuideRow key={g.masterpiece_id} g={g} num={i + 1} />)}
            {important.length > 0 ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" color="text.secondary">Also notable</Typography>
                {important.map((g) => <GuideRow key={g.masterpiece_id} g={g} num={null} />)}
              </>
            ) : null}
          </Stack>
        </Box>
      ) : null}

      {outputs.length > 0 ? (
        <Box sx={{ mt: 5 }}>
          <Typography variant="overline" color="text.secondary">Memoir</Typography>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {outputs.map((o) => (
              <Box key={o.output_id} sx={{ bgcolor: 'primary.main', color: '#E8F4EC', p: 3, borderRadius: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    opacity: 0.6,
                    display: 'block',
                    mb: 1,
                  }}
                >
                  {o.prompt_type.replace(/_/g, ' ')}
                </Typography>
                <Typography sx={{ fontFamily: serifFont, fontStyle: 'italic', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {renderRich(o.content)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}

function GuideRow({ g, num }: { g: GuideItem; num: number | null }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box
        sx={{
          flex: '0 0 32px',
          fontFamily: serifFont,
          fontStyle: 'italic',
          color: 'primary.main',
          fontSize: 22,
          fontWeight: 700,
        }}
      >
        {num ?? '·'}
      </Box>
      {g.image_url ? (
        <Box sx={{ flex: '0 0 120px' }}>
          <img
            src={g.image_url}
            alt=""
            loading="lazy"
            style={{
              width: 120,
              height: 120,
              objectFit: 'cover',
              borderRadius: 6,
              display: 'block',
              imageOrientation: 'from-image',
            }}
          />
        </Box>
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600 }}>{g.name}</Typography>
        {g.artist ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {g.artist}{g.date_or_period ? ` · ${g.date_or_period}` : ''}
          </Typography>
        ) : null}
        {g.instruction ? <Typography variant="body2" sx={{ mt: 0.5 }}>{g.instruction}</Typography> : null}
        {g.why_it_matters ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {g.why_it_matters}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
